import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';

const BRANDS: Record<string, string> = { hikvision: 'Hikvision', dahua: 'Dahua', zkteco: 'ZKTeco', tvt: 'TVT / Webcam' };
const DIRECTIONS: Record<string, string> = { entry: 'Орох', exit: 'Гарах' };
const ANPR_ACTIONS: Record<string, { label: string; color: string }> = {
  allowed: { label: 'Зөвшөөрсөн', color: '#10b981' },
  denied: { label: 'Татгалзсан', color: '#ef4444' },
  guest_allowed: { label: 'Зочин', color: '#3b82f6' },
  manual_open: { label: 'Гар удирдлага', color: '#f59e0b' },
};

const SPOT_TYPES: Record<string, string> = {
  indoor: 'Дотор', outdoor: 'Гадна', underground: 'Газар доорх', covered: 'Бүтээлэгтэй',
};

const SPOT_STATUSES: Record<string, string> = {
  available: 'Сул', occupied: 'Эзэлсэн', reserved: 'Захиалсан', maintenance: 'Засварт',
};

const STATUS_COLORS: Record<string, string> = {
  available: '#236844', occupied: '#3b82f6', reserved: '#f59e0b', maintenance: '#ef4444',
};

/** Гаражийн дугааруудыг задлана: таслал/зай/мөр тусгаарлана, "1-24" зэрэг
 *  тоон мужийг дэлгэнэ. Жишээ: "1-3, 5A, 10" → ["1","2","3","5A","10"]. */
function parseSpotNumbers(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const tokenRaw of (text || '').split(/[\s,;\n]+/)) {
    const token = tokenRaw.trim();
    if (!token) continue;
    const range = token.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      let a = parseInt(range[1], 10), b = parseInt(range[2], 10);
      if (a > b) [a, b] = [b, a];
      if (b - a > 999) b = a + 999; // хамгаалалт
      for (let i = a; i <= b; i++) { const s = String(i); if (!seen.has(s)) { seen.add(s); out.push(s); } }
    } else if (!seen.has(token)) {
      seen.add(token); out.push(token);
    }
  }
  return out;
}

interface Stats {
  total_lots: number;
  total_spots: number;
  occupied_spots: number;
  available_spots: number;
  total_vehicles: number;
}

export default function ParkingPage() {
  const [tab, setTab] = useState<'vehicles' | 'lots' | 'cameras' | 'events'>('vehicles');
  const [stats, setStats] = useState<Stats | null>(null);

  // ── vehicles state ──
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [vTotal, setVTotal] = useState(0);
  const [vLoading, setVLoading] = useState(true);
  const [vPage, setVPage] = useState(0);
  const V_PAGE_SIZE = 50;
  const [vSearch, setVSearch] = useState('');
  const [showVForm, setShowVForm] = useState(false);
  const [editV, setEditV] = useState<any>(null);
  const [vForm, setVForm] = useState({
    unit_id: '', plate_number: '', body_type: '', make: '', model: '', color: '',
    owner_name: '', owner_phone: '', spot_id: '', notes: '', has_garage: true,
    valid_from: '', valid_until: '',
  });
  const [vehicleRef, setVehicleRef] = useState<{ body_types: string[]; makes: { name: string; models: string[] }[] }>({ body_types: [], makes: [] });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [makeOther, setMakeOther] = useState(false);
  const [modelOther, setModelOther] = useState(false);
  // Байр → Орц → Тоот шаталсан сонголт
  const [vBuilding, setVBuilding] = useState('');
  const [vEntrance, setVEntrance] = useState('');

  // ── lots state ──
  const [lots, setLots] = useState<any[]>([]);
  const [lLoading, setLLoading] = useState(true);
  const [showLForm, setShowLForm] = useState(false);
  const [editL, setEditL] = useState<any>(null);
  const [lForm, setLForm] = useState({
    name: '', location: '', total_spots: 0, spot_type: 'outdoor', monthly_fee: '' as number | '', notes: '',
    spot_numbers: '',
  });

  // ── spots state ──
  const [selectedLot, setSelectedLot] = useState<any>(null);
  const [spots, setSpots] = useState<any[]>([]);
  const [allSpots, setAllSpots] = useState<any[]>([]);   // машины формд дугаараар сонгох
  const [showSpotForm, setShowSpotForm] = useState(false);
  const [spotForm, setSpotForm] = useState({ spot_number: '', unit_id: '', notes: '' });

  // ── cameras state ──
  const [cameras, setCameras] = useState<any[]>([]);
  const [camLoading, setCamLoading] = useState(true);
  const [showCamForm, setShowCamForm] = useState(false);
  const [editCam, setEditCam] = useState<any>(null);
  const [camForm, setCamForm] = useState({
    lot_id: '', name: '', brand: 'hikvision', direction: 'entry',
    ip_address: '', port: 80, username: 'admin', password: '', webhook_secret: '',
  });
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // ── events state ──
  const [events, setEvents] = useState<any[]>([]);
  const [evLoading, setEvLoading] = useState(true);
  const [evPlate, setEvPlate] = useState('');
  const [evAction, setEvAction] = useState('');

  // ── units lookup ──
  const [units, setUnits] = useState<any[]>([]);

  const vBuildings = Array.from(new Set(units.map((u: any) => u.building).filter(Boolean)))
    .sort((a: any, b: any) => String(a).localeCompare(String(b), 'mn', { numeric: true }));
  const vEntrances = Array.from(new Set(units
    .filter((u: any) => u.building === vBuilding)
    .map((u: any) => u.entrance).filter(Boolean)))
    .sort((a: any, b: any) => String(a).localeCompare(String(b), 'mn', { numeric: true }));
  const vFilteredUnits = units
    .filter((u: any) => u.building === vBuilding && (!vEntrance || u.entrance === vEntrance))
    .sort((a: any, b: any) => String(a.unit_number).localeCompare(String(b.unit_number), 'mn', { numeric: true }));


  useEffect(() => {
    loadStats();
    loadUnits();
    api.get<any>('/vehicle-reference')
      .then(r => setVehicleRef({ body_types: r.body_types || [], makes: r.makes || [] }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'vehicles') { loadVehicles(); loadAllSpots(); }
    else if (tab === 'lots') loadLots();
    else if (tab === 'cameras') { loadCameras(); loadLots(); }
    else if (tab === 'events') loadEvents();
  }, [tab]);

  const loadStats = async () => {
    try { const r = await api.get<any>('/admin/parking/stats'); setStats(r); } catch {}
  };

  const loadUnits = async () => {
    try { const r = await api.get<any>('/admin/units?limit=1000'); setUnits(r.items || []); } catch {}
  };

  const loadVehicles = async () => {
    setVLoading(true);
    try {
      const qs = vSearch ? `?search=${encodeURIComponent(vSearch)}` : '';
      const r = await api.get<any>(`/admin/vehicles${qs}`);
      setVehicles(r.items || []);
      setVTotal(r.total || 0);
      setVPage(0);
    } catch {} finally { setVLoading(false); }
  };

  const loadLots = async () => {
    setLLoading(true);
    try {
      const r = await api.get<any>('/admin/parking-lots');
      setLots(r.items || []);
    } catch {} finally { setLLoading(false); }
  };

  const loadAllSpots = async () => {
    try {
      const r = await api.get<any>('/admin/parking-spots');
      setAllSpots(r.items || []);
    } catch {}
  };

  const loadSpots = async (lotId: string) => {
    try {
      const r = await api.get<any>(`/admin/parking-lots/${lotId}/spots`);
      setSpots(r.items || []);
    } catch {}
  };

  // ── vehicle CRUD ──
  const openVForm = (v?: any) => {
    if (v) {
      setEditV(v);
      setVForm({
        unit_id: v.unit_id || '', plate_number: v.plate_number || '', body_type: v.body_type || '',
        make: v.make || '', model: v.model || '', color: v.color || '',
        owner_name: v.owner_name || '', owner_phone: v.owner_phone || '',
        spot_id: v.spot_id || '', notes: v.notes || '',
        has_garage: v.has_garage !== false,
        valid_from: v.valid_from || '', valid_until: v.valid_until || '',
      });
      const known = vehicleRef.makes.find(m => m.name === v.make);
      setMakeOther(!!v.make && !known);
      setModelOther(!!v.model && (!known || !known.models.includes(v.model)));
      const vu = units.find((u: any) => u.id === v.unit_id);
      setVBuilding(vu?.building || '');
      setVEntrance(vu?.entrance || '');
    } else {
      setEditV(null);
      setVForm({ unit_id: '', plate_number: '', body_type: '', make: '', model: '', color: '', owner_name: '', owner_phone: '', spot_id: '', notes: '', has_garage: true, valid_from: '', valid_until: '' });
      setMakeOther(false);
      setModelOther(false);
      setVBuilding('');
      setVEntrance('');
    }
    setShowVForm(true);
  };

  const saveVehicle = async () => {
    try {
      const payload = { ...vForm, spot_id: vForm.spot_id || undefined };
      if (editV) {
        await api.put(`/admin/vehicles/${editV.id}`, payload);
      } else {
        await api.post('/admin/vehicles', payload);
      }
      setShowVForm(false);
      loadVehicles();
      loadStats();
      loadAllSpots();
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const deleteVehicle = async (id: string) => {
    if (!window.confirm('Тээврийн хэрэгслийг устгах уу?')) return;
    try { await api.delete(`/admin/vehicles/${id}`); loadVehicles(); loadStats(); } catch {}
  };

  // ── lot CRUD ──
  const openLForm = async (l?: any) => {
    if (l) {
      setEditL(l);
      // Одоо байгаа гаражийн дугааруудыг татаж урьдчилан бөглөнө
      let nums = '';
      try {
        const r = await api.get<any>(`/admin/parking-lots/${l.id}/spots`);
        nums = (r.items || []).map((s: any) => s.spot_number).join(', ');
      } catch { /* алгасна */ }
      setLForm({
        name: l.name, location: l.location || '', total_spots: l.total_spots,
        spot_type: l.spot_type || 'outdoor', monthly_fee: l.monthly_fee || '', notes: l.notes || '',
        spot_numbers: nums,
      });
    } else {
      setEditL(null);
      setLForm({ name: '', location: '', total_spots: 0, spot_type: 'outdoor', monthly_fee: '', notes: '', spot_numbers: '' });
    }
    setShowLForm(true);
  };

  const saveLot = async () => {
    try {
      const nums = parseSpotNumbers(lForm.spot_numbers);
      const { spot_numbers, ...rest } = lForm;
      const payload = { ...rest, total_spots: nums.length || lForm.total_spots, monthly_fee: lForm.monthly_fee || undefined };
      let lotId = editL?.id;
      if (editL) {
        await api.put(`/admin/parking-lots/${editL.id}`, payload);
      } else {
        const created = await api.post<any>('/admin/parking-lots', payload);
        lotId = created.id;
      }
      // Гаражийн дугаарууд оруулсан бол дугаартай байрууд үүсгэнэ (давхардлыг алгасна)
      if (lotId && nums.length) {
        await api.post(`/admin/parking-lots/${lotId}/spots/bulk`, { numbers: nums });
      }
      setShowLForm(false);
      loadLots();
      loadStats();
      if (selectedLot && selectedLot.id === lotId) loadSpots(lotId);
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const deleteLot = async (id: string) => {
    if (!window.confirm('Зогсоолыг устгах уу?')) return;
    try { await api.delete(`/admin/parking-lots/${id}`); loadLots(); loadStats(); } catch {}
  };

  // ── spot CRUD ──
  const openSpots = (lot: any) => {
    setSelectedLot(lot);
    loadSpots(lot.id);
  };

  const saveSpot = async () => {
    if (!selectedLot) return;
    try {
      await api.post(`/admin/parking-lots/${selectedLot.id}/spots`, {
        ...spotForm, unit_id: spotForm.unit_id || undefined,
      });
      setShowSpotForm(false);
      setSpotForm({ spot_number: '', unit_id: '', notes: '' });
      loadSpots(selectedLot.id);
      loadStats();
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const deleteSpot = async (id: string) => {
    if (!window.confirm('Зогсоолын байрыг устгах уу?')) return;
    try {
      await api.delete(`/admin/parking-spots/${id}`);
      if (selectedLot) loadSpots(selectedLot.id);
      loadStats();
    } catch {}
  };

  // ── camera CRUD ──
  const loadCameras = async () => {
    setCamLoading(true);
    try { const r = await api.get<any>('/admin/parking/cameras'); setCameras(r.items || []); }
    catch {} finally { setCamLoading(false); }
  };

  const openCamForm = (c?: any) => {
    if (c) {
      setEditCam(c);
      setCamForm({
        lot_id: c.lot_id, name: c.name, brand: c.brand, direction: c.direction,
        ip_address: c.ip_address, port: c.port, username: c.username,
        password: '', webhook_secret: c.webhook_secret || '',
      });
    } else {
      setEditCam(null);
      setCamForm({ lot_id: '', name: '', brand: 'hikvision', direction: 'entry', ip_address: '', port: 80, username: 'admin', password: '', webhook_secret: '' });
    }
    setTestResult(null);
    setShowCamForm(true);
  };

  const saveCamera = async () => {
    try {
      if (editCam) {
        const payload: any = { ...camForm };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/parking/cameras/${editCam.id}`, payload);
      } else {
        await api.post('/admin/parking/cameras', camForm);
      }
      setShowCamForm(false);
      loadCameras();
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const deleteCamera = async (id: string) => {
    if (!window.confirm('Камер устгах уу?')) return;
    try { await api.delete(`/admin/parking/cameras/${id}`); loadCameras(); } catch {}
  };

  const testCamera = async (id: string) => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post<any>(`/admin/parking/cameras/${id}/test`);
      setTestResult(r);
    } catch (e: any) { setTestResult({ error: e?.body || 'Холбогдож чадсангүй' }); }
    finally { setTesting(false); }
  };

  const openBarrier = async (camId: string) => {
    if (!window.confirm('Хаалтыг нээх үү?')) return;
    try {
      const r = await api.post<any>(`/admin/parking/barrier/open?camera_id=${camId}`);
      if (r?.opened) {
        alert('Хаалт нээгдлээ');
        return;
      }
      if (r?.queued_command_id) {
        // Команд bridge рүү дарааллуулсан — гүйцэтгэлийг нь хэдэн секунд хүлээж шалгана
        for (let i = 0; i < 6; i++) {
          await new Promise(res => setTimeout(res, 2000));
          try {
            const s = await api.get<any>(`/admin/parking/barrier/status/${r.queued_command_id}`);
            if (!s.pending) {
              alert(s.ok ? 'Хаалт нээгдлээ (bridge-ээр)' : `Хаалт нээгдсэнгүй: ${s.detail || 'камер татгалзав'}`);
              return;
            }
          } catch {}
        }
        alert('Команд илгээгдсэн ч bridge хариу өгсөнгүй — камерын сүлжээн дэх bridge програм ажиллаж байгаа эсэхийг шалгана уу.');
        return;
      }
      alert('Хаалт нээгдсэнгүй: сервер камерт хүрч чадсангүй.');
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  // ── events ──
  const loadEvents = async () => {
    setEvLoading(true);
    try {
      let qs = '?per_page=100';
      if (evPlate) qs += `&plate=${encodeURIComponent(evPlate)}`;
      if (evAction) qs += `&action=${evAction}`;
      const r = await api.get<any>(`/admin/parking/anpr-events${qs}`);
      setEvents(r.items || []);
    } catch {} finally { setEvLoading(false); }
  };

  // Камерын Vehicle Database export (xlsx/csv) файлыг импортлох
  const importVehicleFile = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${api.baseUrl}/admin/vehicles/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${api.getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Импорт амжилтгүй');
      setImportResult(data);
      loadVehicles();
    } catch (e: any) {
      setImportResult({ error: e.message || 'Импорт амжилтгүй' });
    } finally {
      setImporting(false);
    }
  };

  const getUnitLabel = (unitId: string | null) => {
    if (!unitId) return '—';
    const u = units.find((u: any) => u.id === unitId);
    return u ? `${u.building || ''}-${u.unit_number}` : unitId.substring(0, 8);
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 14, padding: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e8e4db',
  };

  const btnPrimary: React.CSSProperties = {
    padding: '10px 20px', background: 'linear-gradient(135deg, #2f7d52, #236844)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontWeight: 600, fontSize: 14,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1px solid #d5d0c4', fontSize: 14, background: '#faf9f5',
  };

  const modalOverlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };

  const modalContent: React.CSSProperties = {
    background: '#fff', borderRadius: 16, padding: 28, width: '95%', maxWidth: 560,
    maxHeight: '90vh', overflowY: 'auto',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Inter'", fontSize: 26, fontWeight: 700, color: '#1a3a2a', margin: 0 }}>
            Автозогсоол & Тээврийн хэрэгсэл
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>Автомашины бүртгэл, зогсоолын удирдлага</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Нийт зогсоол', value: stats.total_lots, color: '#236844' },
            { label: 'Нийт байр', value: stats.total_spots, color: '#2f7d52' },
            { label: 'Эзэлсэн', value: stats.occupied_spots, color: '#3b82f6' },
            { label: 'Сул байр', value: stats.available_spots, color: '#10b981' },
            { label: 'Бүртгэлтэй машин', value: stats.total_vehicles, color: '#f59e0b' },
          ].map((s, i) => (
            <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color, fontFamily: "'Inter'" }}>{s.value}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#e8e4db', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {([
          ['vehicles', 'Тээврийн хэрэгсэл'],
          ['lots', 'Зогсоолууд'],
          ['cameras', 'Камерууд'],
          ['events', 'ANPR лог'],
        ] as const).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t as any)} style={{
            padding: '10px 24px', border: 'none', borderRadius: 10, cursor: 'pointer',
            background: tab === t ? '#fff' : 'transparent',
            color: tab === t ? '#236844' : '#6b7280',
            fontWeight: tab === t ? 600 : 400, fontSize: 14,
            boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ═══════════ VEHICLES TAB ═══════════ */}
      {tab === 'vehicles' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input
                placeholder="Улсын дугаараар хайх..."
                value={vSearch} onChange={e => setVSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadVehicles()}
                style={{ ...inputStyle, width: 280 }}
              />
              <button onClick={loadVehicles} style={{ ...btnPrimary, background: '#e8e4db', color: '#1a3a2a' }}>Хайх</button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...btnPrimary, background: '#e8e4db', color: '#1a3a2a', cursor: importing ? 'wait' : 'pointer', display: 'inline-block' }}>
                {importing ? 'Импортолж байна...' : '⬆ Камераас импорт'}
                <input
                  type="file" accept=".xlsx,.xlsm,.csv" style={{ display: 'none' }} disabled={importing}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) importVehicleFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
              <button onClick={() => openVForm()} style={btnPrimary}>+ Машин бүртгэх</button>
            </div>
          </div>

          {importResult && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', borderRadius: 10, fontSize: 14,
              background: importResult.error ? '#fef2f2' : '#f0fdf4',
              color: importResult.error ? '#b91c1c' : '#166534',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>
                {importResult.error
                  ? importResult.error
                  : `Импорт дууслаа: ${importResult.created} шинэ, ${importResult.updated} шинэчилсэн, ${importResult.skipped} алгассан (нийт ${importResult.total_rows} мөр)`}
                {!importResult.error && importResult.errors?.length > 0 && ` — ${importResult.errors.length} мөрөнд алдаа`}
              </span>
              <button onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>×</button>
            </div>
          )}

          {vLoading ? <p style={{ color: '#6b7280' }}>Уншиж байна...</p> : (
           <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8e4db' }}>
                  {['№', 'Улсын дугаар', 'Тоот', 'Марк', 'Өнгө', 'Эзэмшигч', 'Утас', 'Бүртгэсэн', ''].map((h, i) => (
                    <th key={i} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicles.slice(vPage * V_PAGE_SIZE, (vPage + 1) * V_PAGE_SIZE).map((v, i) => (
                  <tr key={v.id} style={{ borderBottom: '1px solid #f0ede5' }}>
                    <td style={{ padding: '12px 8px', fontSize: 14, color: '#8a978f' }}>{vPage * V_PAGE_SIZE + i + 1}</td>
                    <td style={{ padding: '12px 8px', fontWeight: 700, fontSize: 15, fontFamily: "'Inter'", color: '#1a3a2a' }}>{v.plate_number}</td>
                    <td style={{ padding: '12px 8px', fontSize: 14 }}>{getUnitLabel(v.unit_id)}</td>
                    <td style={{ padding: '12px 8px', fontSize: 14 }}>{[v.make, v.model].filter(Boolean).join(' ') || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 14 }}>{v.color || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 14 }}>{v.owner_name || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 14 }}>{v.owner_phone || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#6b7280' }}>{v.registered_at || '-'}</td>
                    <td style={{ padding: '12px 8px', display: 'flex', gap: 8 }}>
                      <button onClick={() => openVForm(v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2f7d52', fontSize: 13, fontWeight: 500 }}>Засах</button>
                      <button onClick={() => deleteVehicle(v.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500 }}>Устгах</button>
                    </td>
                  </tr>
                ))}
                {vehicles.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Бүртгэлтэй тээврийн хэрэгсэл байхгүй</td></tr>
                )}
              </tbody>
            </table>
            {vehicles.length > 0 && (
              <Pagination
                page={vPage}
                pageSize={V_PAGE_SIZE}
                total={vehicles.length}
                count={vehicles.slice(vPage * V_PAGE_SIZE, (vPage + 1) * V_PAGE_SIZE).length}
                onChange={setVPage}
              />
            )}
           </>
          )}
        </div>
      )}

      {/* ═══════════ LOTS TAB ═══════════ */}
      {tab === 'lots' && !selectedLot && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => openLForm()} style={btnPrimary}>+ Зогсоол нэмэх</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {lots.map(l => (
              <div key={l.id} style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 17, fontFamily: "'Inter'", fontWeight: 700, color: '#1a3a2a' }}>{l.name}</h3>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{l.location || 'Байршил тодорхойгүй'}</p>
                  </div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: l.is_active ? '#dcfce7' : '#fef2f2',
                    color: l.is_active ? '#236844' : '#ef4444',
                  }}>
                    {l.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, margin: '16px 0' }}>
                  <div style={{ textAlign: 'center', padding: 10, background: '#f4f1e8', borderRadius: 10 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1a3a2a' }}>{l.total_spots}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Нийт</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 10, background: '#dbeafe', borderRadius: 10 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#3b82f6' }}>{l.occupied_spots}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Эзэлсэн</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 10, background: '#dcfce7', borderRadius: 10 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#236844' }}>{l.available_spots}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Сул</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                  <span style={{ color: '#6b7280' }}>
                    {SPOT_TYPES[l.spot_type] || l.spot_type}
                    {l.monthly_fee ? ` · ₮${Number(l.monthly_fee).toLocaleString()}/сар` : ''}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openSpots(l)} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 13 }}>Байрууд</button>
                    <button onClick={() => openLForm(l)} style={{ background: 'none', border: '1px solid #d5d0c4', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Засах</button>
                    <button onClick={() => deleteLot(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500 }}>Устгах</button>
                  </div>
                </div>
              </div>
            ))}
            {!lLoading && lots.length === 0 && (
              <div style={{ ...cardStyle, textAlign: 'center', color: '#6b7280', padding: 60, gridColumn: '1/-1' }}>
                Бүртгэлтэй зогсоол байхгүй
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ SPOTS VIEW ═══════════ */}
      {tab === 'lots' && selectedLot && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => setSelectedLot(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6b7280' }}>←</button>
              <div>
                <h3 style={{ margin: 0, fontFamily: "'Inter'", fontSize: 18, color: '#1a3a2a' }}>{selectedLot.name}</h3>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Зогсоолын байрууд</span>
              </div>
            </div>
            <button onClick={() => { setSpotForm({ spot_number: '', unit_id: '', notes: '' }); setShowSpotForm(true); }} style={btnPrimary}>+ Байр нэмэх</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {spots.map(s => (
              <div key={s.id} style={{
                background: '#faf9f5', borderRadius: 12, padding: 14,
                border: `2px solid ${STATUS_COLORS[s.status] || '#d5d0c4'}`,
                position: 'relative',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 16, fontFamily: "'Inter'", color: '#1a3a2a' }}>{s.spot_number}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: STATUS_COLORS[s.status] + '18',
                    color: STATUS_COLORS[s.status],
                  }}>{SPOT_STATUSES[s.status]}</span>
                </div>
                {s.unit_id && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Тоот: {getUnitLabel(s.unit_id)}</div>}
                {s.vehicles?.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {s.vehicles.map((v: any) => (
                      <div key={v.id} style={{ fontSize: 12, color: '#2f7d52', fontWeight: 600 }}>🚗 {v.plate_number}</div>
                    ))}
                  </div>
                )}
                <button onClick={() => deleteSpot(s.id)} style={{
                  position: 'absolute', top: 6, right: 6, background: 'none', border: 'none',
                  cursor: 'pointer', color: '#d1d5db', fontSize: 14,
                }}>✕</button>
              </div>
            ))}
            {spots.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#6b7280', padding: 40 }}>
                Байр бүртгэгдээгүй байна
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ CAMERAS TAB ═══════════ */}
      {tab === 'cameras' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontFamily: "'Inter'", fontSize: 18, fontWeight: 700, color: '#1a3a2a' }}>ANPR камерууд</h3>
            <button onClick={() => openCamForm()} style={btnPrimary}>+ Камер нэмэх</button>
          </div>

          {camLoading ? <p style={{ color: '#6b7280' }}>Уншиж байна...</p> : cameras.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📷</div>
              <p style={{ fontSize: 15, marginBottom: 8 }}>Бүртгэлтэй камер байхгүй</p>
              <p style={{ fontSize: 13 }}>Автозогсоолын хаалтны камераа бүртгэж тохируулаарай</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
              {cameras.map((c: any) => (
                <div key={c.id} style={{ background: '#faf9f5', borderRadius: 14, padding: 20, border: '1px solid #e8e4db' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a3a2a' }}>{c.name}</h4>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}>
                          {BRANDS[c.brand] || c.brand}
                        </span>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: c.direction === 'entry' ? '#dcfce7' : '#fef3c7', color: c.direction === 'entry' ? '#236844' : '#92400e' }}>
                          {DIRECTIONS[c.direction] || c.direction}
                        </span>
                      </div>
                    </div>
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: c.is_active ? '#10b981' : '#d1d5db',
                      display: 'inline-block', marginTop: 4,
                    }} title={c.is_active ? 'Идэвхтэй' : 'Идэвхгүй'} />
                  </div>

                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                    <div>IP: <strong style={{ color: '#374151' }}>{c.ip_address}:{c.port}</strong></div>
                    {c.last_heartbeat && <div>Сүүлд: {new Date(c.last_heartbeat).toLocaleString('mn-MN')}</div>}
                  </div>

                  <div style={{ background: '#f4f1e8', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Webhook URL (камерын Alarm/HTTP push тохиргоонд оруулна):</div>
                    <code style={{ fontSize: 12, color: '#236844', wordBreak: 'break-all' }}>
                      {api.baseUrl}{c.webhook_url.replace('/api/v1', '')}{c.webhook_secret ? `?secret=${c.webhook_secret}` : ''}
                    </code>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => testCamera(c.id)} disabled={testing} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 13, background: '#2563eb' }}>
                      {testing ? '...' : 'Тест'}
                    </button>
                    <button onClick={() => openBarrier(c.id)} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 13, background: '#f59e0b' }}>Хаалт нээх</button>
                    <button onClick={() => openCamForm(c)} style={{ background: 'none', border: '1px solid #d5d0c4', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Засах</button>
                    <button onClick={() => deleteCamera(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500 }}>Устгах</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {testResult && (
            <div style={{ marginTop: 16, padding: 16, background: testResult.error ? '#fef2f2' : '#f0fdf4', borderRadius: 12, border: `1px solid ${testResult.error ? '#fecaca' : '#bbf7d0'}` }}>
              <strong>{testResult.error ? 'Алдаа' : 'Амжилттай'}: </strong>
              {testResult.error || `${testResult.camera} — ${testResult.health?.status}, хаалт: ${testResult.barrier_test ? 'нээгдлээ' : 'тест хийгдсэнгүй'}`}
            </div>
          )}
        </div>
      )}

      {/* ═══════════ EVENTS TAB ═══════════ */}
      {tab === 'events' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontFamily: "'Inter'", fontSize: 18, fontWeight: 700, color: '#1a3a2a' }}>ANPR нэвтрэлтийн лог</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input placeholder="Улсын дугаар..." value={evPlate} onChange={e => setEvPlate(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadEvents()} style={{ ...inputStyle, width: 200 }} />
              <select value={evAction} onChange={e => { setEvAction(e.target.value); }} style={{ ...inputStyle, width: 160 }}>
                <option value="">Бүх үйлдэл</option>
                <option value="allowed">Зөвшөөрсөн</option>
                <option value="denied">Татгалзсан</option>
                <option value="guest_allowed">Зочин</option>
                <option value="manual_open">Гар удирдлага</option>
              </select>
              <button onClick={loadEvents} style={{ ...btnPrimary, padding: '10px 20px' }}>Шүүх</button>
            </div>
          </div>

          {evLoading ? <p style={{ color: '#6b7280' }}>Уншиж байна...</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8e4db' }}>
                  {['Огноо', 'Улсын дугаар', 'Чиглэл', 'Үйлдэл', 'Итгэлцүүр', 'Зочны ID'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((e: any) => {
                  const act = ANPR_ACTIONS[e.action] || { label: e.action, color: '#6b7280' };
                  return (
                    <tr key={e.id} style={{ borderBottom: '1px solid #f0ede5' }}>
                      <td style={{ padding: '10px 8px', fontSize: 13, color: '#6b7280' }}>{new Date(e.event_time).toLocaleString('mn-MN')}</td>
                      <td style={{ padding: '10px 8px', fontWeight: 700, fontSize: 15, fontFamily: "'Inter'", color: '#1a3a2a', letterSpacing: 1 }}>{e.plate_number}</td>
                      <td style={{ padding: '10px 8px', fontSize: 13 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: e.direction === 'entry' ? '#dcfce7' : '#fef3c7', color: e.direction === 'entry' ? '#236844' : '#92400e' }}>
                          {DIRECTIONS[e.direction] || e.direction}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: act.color + '18', color: act.color }}>
                          {act.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: 13, color: '#6b7280' }}>{e.confidence ? `${e.confidence}%` : '-'}</td>
                      <td style={{ padding: '10px 8px', fontSize: 12, color: '#6b7280' }}>{e.guest_pass_id ? e.guest_pass_id.substring(0, 8) : '-'}</td>
                    </tr>
                  );
                })}
                {events.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Нэвтрэлтийн бүртгэл байхгүй</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ═══════════ CAMERA MODAL ═══════════ */}
      {showCamForm && (
        <div style={modalOverlay} onClick={() => setShowCamForm(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1a3a2a', marginTop: 0 }}>
              {editCam ? 'Камер засах' : 'Камер нэмэх'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Камерын нэр *</label>
                <input value={camForm.name} onChange={e => setCamForm({ ...camForm, name: e.target.value })} style={inputStyle} placeholder="Орох хаалга Cam-1" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Зогсоол *</label>
                <SearchableSelect
                  options={lots.map((l: any) => ({ value: l.id, label: l.name }))}
                  value={camForm.lot_id}
                  onChange={v => setCamForm({ ...camForm, lot_id: v })}
                  placeholder="Хайх: зогсоол"
                  allowClear={false}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Брэнд *</label>
                <SearchableSelect
                  options={Object.entries(BRANDS).map(([k, v]) => ({ value: k, label: v as string }))}
                  value={camForm.brand}
                  onChange={v => setCamForm({ ...camForm, brand: v })}
                  placeholder="Хайх: брэнд"
                  allowClear={false}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Чиглэл *</label>
                <SearchableSelect
                  options={Object.entries(DIRECTIONS).map(([k, v]) => ({ value: k, label: v as string }))}
                  value={camForm.direction}
                  onChange={v => setCamForm({ ...camForm, direction: v })}
                  placeholder="Хайх: чиглэл"
                  allowClear={false}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>IP хаяг *</label>
                <input value={camForm.ip_address} onChange={e => setCamForm({ ...camForm, ip_address: e.target.value })} style={inputStyle} placeholder="192.168.1.100" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Порт</label>
                <input type="number" value={camForm.port} onChange={e => setCamForm({ ...camForm, port: Number(e.target.value) })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Нэвтрэх нэр</label>
                <input value={camForm.username} onChange={e => setCamForm({ ...camForm, username: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Нууц үг</label>
                <input type="password" value={camForm.password} onChange={e => setCamForm({ ...camForm, password: e.target.value })} style={inputStyle} placeholder={editCam ? '(өөрчлөхгүй бол хоосон)' : ''} />
              </div>
            </div>

            {!editCam && (
              <div style={{ marginTop: 16, padding: 14, background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginBottom: 6 }}>Тохируулах заавар:</div>
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                  <li>Камерын веб интерфейс рүү нэвтрэх ({camForm.ip_address || 'IP'}:{camForm.port || 80})</li>
                  <li>ANPR / License Plate Recognition идэвхжүүлэх</li>
                  <li>Webhook / HTTP notification тохируулж, доорх URL оруулах</li>
                  <li>Хадгалсны дараа "Тест" товч дарж холболт шалгах</li>
                </ol>
                {camForm.brand === 'tvt' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                    <strong>TVT / Webcam камерт:</strong> Config → Alarm → Alarm Server хэсэгт серверийн
                    хаяг/портыг оруулна. Мөн Event → License Plate Detection → Vehicle Database → Export
                    хийж гарсан Excel файлыг "Машинууд" таб дахь <em>Камераас импорт</em> товчоор оруулж
                    бүртгэлээ шилжүүлж болно. Событийн формат тодорхойгүй бол Alarm Server-ийг түр{' '}
                    <code style={{ wordBreak: 'break-all' }}>{api.baseUrl}/parking/anpr/capture</code> руу
                    зааж ирсэн payload-ыг шалгаарай.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCamForm(false)} style={{ padding: '10px 20px', border: '1px solid #d5d0c4', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Болих</button>
              <button onClick={saveCamera} disabled={!camForm.name || !camForm.lot_id || !camForm.ip_address} style={{ ...btnPrimary, opacity: (!camForm.name || !camForm.lot_id || !camForm.ip_address) ? 0.5 : 1 }}>Хадгалах</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ VEHICLE MODAL ═══════════ */}
      {showVForm && (
        <div style={modalOverlay} onClick={() => setShowVForm(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1a3a2a', marginTop: 0 }}>
              {editV ? 'Машин засах' : 'Машин бүртгэх'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Улсын дугаар *</label>
                <input value={vForm.plate_number} onChange={e => setVForm({ ...vForm, plate_number: e.target.value })} style={inputStyle} placeholder="0000 УБА" />
              </div>
              {/* Байр → Орц → Тоот шаталсан сонголт: олон байртай СӨХ-д бүх
                  тоотыг нэг жагсаалтад асгахад ялгахад хэцүү байсан. */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Байр *</label>
                <SearchableSelect
                  options={vBuildings.map(b => ({ value: b, label: b }))}
                  value={vBuilding}
                  onChange={v => { setVBuilding(v); setVEntrance(''); setVForm({ ...vForm, unit_id: '' }); }}
                  placeholder="Хайх: байр"
                  allowClear={false}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Орц</label>
                {vEntrances.length === 0 ? (
                  <input value={vBuilding ? '(орцгүй)' : ''} disabled placeholder="Эхлээд байраа сонго"
                    style={{ ...inputStyle, background: '#f3f4f6', color: '#9ca3af' }} />
                ) : (
                  <SearchableSelect
                    options={vEntrances.map(en => ({ value: en, label: en }))}
                    value={vEntrance}
                    onChange={v => { setVEntrance(v); setVForm({ ...vForm, unit_id: '' }); }}
                    placeholder="Бүх орц"
                    allowClear
                    clearLabel="Бүх орц"
                  />
                )}
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Тоот *</label>
                {!vBuilding ? (
                  <input value="" disabled placeholder="Эхлээд байраа сонгоно уу"
                    style={{ ...inputStyle, background: '#f3f4f6', color: '#9ca3af' }} />
                ) : (
                  <SearchableSelect
                    options={vFilteredUnits.map((u: any) => ({
                      value: u.id,
                      label: `${u.unit_number}${u.entrance && !vEntrance ? ` (${u.entrance}-р орц)` : ''}`,
                    }))}
                    value={vForm.unit_id}
                    onChange={v => setVForm({ ...vForm, unit_id: v })}
                    placeholder="Хайх: тоот"
                    allowClear={false}
                  />
                )}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Төрөл</label>
                <SearchableSelect
                  options={vehicleRef.body_types.map(bt => ({ value: bt, label: bt }))}
                  value={vForm.body_type}
                  onChange={v => setVForm({ ...vForm, body_type: v })}
                  placeholder="Хайх: төрөл"
                  clearLabel="-- Төрөл сонгох --"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Марк</label>
                <SearchableSelect
                  options={[
                    ...vehicleRef.makes.filter(m => m.name !== 'Бусад').map(m => ({ value: m.name, label: m.name })),
                    { value: '__other__', label: 'Бусад (гараар)' },
                  ]}
                  value={makeOther ? '__other__' : vForm.make}
                  onChange={val => {
                    if (val === '__other__') { setMakeOther(true); setModelOther(true); setVForm({ ...vForm, make: '', model: '' }); }
                    else { setMakeOther(false); setModelOther(false); setVForm({ ...vForm, make: val, model: '' }); }
                  }}
                  placeholder="Хайх: марк"
                  clearLabel="-- Марк сонгох --"
                />
                {makeOther && (
                  <input value={vForm.make} onChange={e => setVForm({ ...vForm, make: e.target.value })}
                    style={{ ...inputStyle, marginTop: 6 }} placeholder="Маркаа бичнэ үү" />
                )}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Загвар</label>
                {(() => {
                  const models = vehicleRef.makes.find(m => m.name === vForm.make)?.models || [];
                  const useSelect = !makeOther && models.length > 0;
                  if (!useSelect) {
                    return (
                      <input value={vForm.model} onChange={e => setVForm({ ...vForm, model: e.target.value })}
                        style={inputStyle} placeholder="Prius"
                        disabled={!vForm.make && !makeOther} />
                    );
                  }
                  return (
                    <>
                      <SearchableSelect
                        options={[
                          ...models.map(md => ({ value: md, label: md })),
                          { value: '__other__', label: 'Бусад (гараар)' },
                        ]}
                        value={modelOther ? '__other__' : vForm.model}
                        onChange={val => {
                          if (val === '__other__') { setModelOther(true); setVForm({ ...vForm, model: '' }); }
                          else { setModelOther(false); setVForm({ ...vForm, model: val }); }
                        }}
                        placeholder="Хайх: загвар"
                        clearLabel="-- Загвар сонгох --"
                      />
                      {modelOther && (
                        <input value={vForm.model} onChange={e => setVForm({ ...vForm, model: e.target.value })}
                          style={{ ...inputStyle, marginTop: 6 }} placeholder="Загвараа бичнэ үү" />
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Өнгө</label>
                <input value={vForm.color} onChange={e => setVForm({ ...vForm, color: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Эзэмшигч</label>
                <input value={vForm.owner_name} onChange={e => setVForm({ ...vForm, owner_name: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Утас</label>
                <input value={vForm.owner_phone} onChange={e => setVForm({ ...vForm, owner_phone: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Хаалга (граш) ашиглах эсэх</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: '1.5px solid #dcdacf', borderRadius: 10, background: '#fbfaf6', cursor: 'pointer' }}>
                  <input type="checkbox" checked={vForm.has_garage} onChange={e => setVForm({ ...vForm, has_garage: e.target.checked })} style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: 14, color: '#1c2b22' }}>
                    Граштай — камерын системд нэмж, хаалга автомат нээгдэнэ
                  </span>
                </label>
                <div style={{ fontSize: 12, color: '#8a9a90', marginTop: 4 }}>
                  Тэмдэглэхгүй бол зөвхөн гадна талбайн машин гэж үзэж камерт нэмэхгүй.
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Эхлэх огноо</label>
                <input type="date" value={vForm.valid_from} onChange={e => setVForm({ ...vForm, valid_from: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Дуусах огноо</label>
                <input type="date" value={vForm.valid_until} onChange={e => setVForm({ ...vForm, valid_until: e.target.value })} style={inputStyle} />
                <div style={{ fontSize: 12, color: '#8a9a90', marginTop: 4 }}>Хоосон бол хугацаагүй (байнгын).</div>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Зогсоолын дугаар (граж)</label>
                <SearchableSelect
                  options={(() => {
                    const multiLot = new Set(allSpots.map((s: any) => s.lot_id)).size > 1;
                    return allSpots.map((s: any) => ({
                      value: s.id,
                      label: `${multiLot && s.lot_name ? s.lot_name + ' — ' : ''}${s.spot_number}`
                        + (s.status && s.status !== 'available' && s.id !== vForm.spot_id ? ' (эзэлсэн)' : ''),
                    }));
                  })()}
                  value={vForm.spot_id}
                  onChange={v => setVForm({ ...vForm, spot_id: v })}
                  placeholder="Хайх: зогсоолын дугаар"
                  clearLabel="-- Зогсоолгүй --"
                />
                <div style={{ fontSize: 12, color: '#8a9a90', marginTop: 4 }}>
                  Энэ машин аль дугаартай гаражид хамаарахыг сонгоно. Зогсоолыг «Зогсоолууд» табаас бүртгэнэ.
                </div>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Тэмдэглэл</label>
                <textarea value={vForm.notes} onChange={e => setVForm({ ...vForm, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowVForm(false)} style={{ padding: '10px 20px', border: '1px solid #d5d0c4', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Болих</button>
              <button onClick={saveVehicle} disabled={!vForm.plate_number || !vForm.unit_id} style={{ ...btnPrimary, opacity: (!vForm.plate_number || !vForm.unit_id) ? 0.5 : 1 }}>Хадгалах</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ LOT MODAL ═══════════ */}
      {showLForm && (
        <div style={modalOverlay} onClick={() => setShowLForm(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1a3a2a', marginTop: 0 }}>
              {editL ? 'Зогсоол засах' : 'Зогсоол нэмэх'}
            </h2>
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Нэр *</label>
                <input value={lForm.name} onChange={e => setLForm({ ...lForm, name: e.target.value })} style={inputStyle} placeholder="А байрны зогсоол" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Байршил</label>
                <input value={lForm.location} onChange={e => setLForm({ ...lForm, location: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Төрөл</label>
                <SearchableSelect
                  options={Object.entries(SPOT_TYPES).map(([k, v]) => ({ value: k, label: v as string }))}
                  value={lForm.spot_type}
                  onChange={v => setLForm({ ...lForm, spot_type: v })}
                  placeholder="Хайх: төрөл"
                  allowClear={false}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>
                  Нийт байр — гаражийн дугаарууд
                </label>
                <textarea
                  value={lForm.spot_numbers}
                  onChange={e => setLForm({ ...lForm, spot_numbers: e.target.value })}
                  style={{ ...inputStyle, minHeight: 64, fontFamily: 'monospace' }}
                  placeholder="Жишээ: 1-24  эсвэл  1, 2, 5A, 10"
                />
                {(() => {
                  const n = parseSpotNumbers(lForm.spot_numbers);
                  return (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Орон сууцны доорх дугаартай гаражуудыг оруулна. Муж («1-24») ба жагсаалт («1,2,5A») дэмжинэ.
                      {n.length > 0 && <span style={{ color: '#236844', fontWeight: 600 }}> — {n.length} байр</span>}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Сарын төлбөр (₮)</label>
                <input type="number" value={lForm.monthly_fee} onChange={e => setLForm({ ...lForm, monthly_fee: e.target.value ? Number(e.target.value) : '' })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Тэмдэглэл</label>
                <textarea value={lForm.notes} onChange={e => setLForm({ ...lForm, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowLForm(false)} style={{ padding: '10px 20px', border: '1px solid #d5d0c4', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Болих</button>
              <button onClick={saveLot} disabled={!lForm.name} style={{ ...btnPrimary, opacity: !lForm.name ? 0.5 : 1 }}>Хадгалах</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ SPOT MODAL ═══════════ */}
      {showSpotForm && (
        <div style={modalOverlay} onClick={() => setShowSpotForm(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1a3a2a', marginTop: 0 }}>Зогсоолын байр нэмэх</h2>
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Байрны дугаар *</label>
                <input value={spotForm.spot_number} onChange={e => setSpotForm({ ...spotForm, spot_number: e.target.value })} style={inputStyle} placeholder="A-01" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Тоот (тогтоосон тоот)</label>
                <SearchableSelect
                  options={units.map((u: any) => ({ value: u.id, label: `${u.building}-${u.unit_number}` }))}
                  value={spotForm.unit_id}
                  onChange={v => setSpotForm({ ...spotForm, unit_id: v })}
                  placeholder="Хайх: блок эсвэл тоот"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Тэмдэглэл</label>
                <textarea value={spotForm.notes} onChange={e => setSpotForm({ ...spotForm, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowSpotForm(false)} style={{ padding: '10px 20px', border: '1px solid #d5d0c4', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Болих</button>
              <button onClick={saveSpot} disabled={!spotForm.spot_number} style={{ ...btnPrimary, opacity: !spotForm.spot_number ? 0.5 : 1 }}>Нэмэх</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
